import type { EventMarket } from "hooks/firestore/betting/useBetting";

/**
 * Sorts markets by priority with lowest first and highest last. Markets with no priority are sorted last.
 * @param a
 * @param b
 */
export const sortByPriority = (
  a: { priority?: number },
  b: { priority?: number },
) => {
  return (
    (typeof a.priority === "number" ? a.priority : Number.MAX_VALUE) -
    (typeof b.priority === "number" ? b.priority : Number.MAX_VALUE)
  );
};

export const checkIfPromotionIsAvailable = (markets: EventMarket[]) => {
  return markets.some((market) => market.promotionIsAvailable);
};

/**
 * The attribute/specifier keys for numerical values that are used to sort markets.
 * There are tried in the order of this array so the more important ones should be first.
 */
const specifiers = [
  "threshold",
  "period",
  "goal",
  "points",
  "handicap",
  //specifiers
  "hcp",
  "total",
  "best_of",
  "roundnr",
  "overnr",
  "goalnr",
  "map",
  "mapnr",
] as Array<keyof EventMarket["attributes"]>;

const getSpecifierNumber = (
  market: EventMarket,
  key: keyof EventMarket["attributes"],
): number => {
  const string = market.attributes?.[key] || market.specifiers?.[key];
  return typeof string === "string" ? parseFloat(string) : 0;
};

const compareAttributes = (
  marketA: EventMarket,
  marketB: EventMarket,
): number => {
  for (const specifierKey of specifiers) {
    const comparison =
      getSpecifierNumber(marketA, specifierKey) -
      getSpecifierNumber(marketB, specifierKey);
    if (comparison !== 0) {
      return comparison;
    }
  }
  return 0;
};

export const sortMarkets = (markets: EventMarket[]) => {
  return [...markets].sort((a, b) => {
    let comparison = sortByPriority(a, b);
    if (comparison !== 0) {
      return comparison;
    }

    if (a.marketType === b.marketType) {
      comparison = compareAttributes(a, b);
    }
    if (comparison !== 0) {
      return comparison;
    }

    return a.name?.localeCompare(b.name);
  });
};
